import { useState, useRef, useEffect, useCallback } from 'react';
import Button from '@/UIlibrary/Button/Button';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { AgentTool, InstructionStep, AgentContextVariable } from '@/pages/CustomerSupport/types';
import {
	X,
	Plus,
	MessageSquare,
	Tool,
	ChevronDown,
	ChevronRight,
	Code,
	GitBranch,
	ZoomIn,
	ZoomOut,
	RotateCcw,
	Maximize
} from 'react-feather';
import { useTranslations } from 'use-intl';

interface ToolInstructionBuilderProps {
	isOpen: boolean;
	onClose: () => void;
	availableTools: AgentTool[];
	contextVariables?: AgentContextVariable[];
	onSave: (params: { steps: InstructionStep[] }) => Promise<void>;
	initialData?: { id?: string; steps: InstructionStep[] };
}

interface NodePosition {
	x: number;
	y: number;
}

interface CanvasState {
	positions: Record<number, NodePosition>;
	zoom: number;
	pan: { x: number; y: number };
}

const STORAGE_KEY_PREFIX = 'flow-canvas-';
const NODE_WIDTH = 300;
const NODE_HEIGHT = 100;
const NODE_SPACING_X = 100;

function getDefaultPositions(stepsCount: number): Record<number, NodePosition> {
	const positions: Record<number, NodePosition> = {};
	const startX = 100;
	const startY = 150;
	for (let i = 0; i < stepsCount; i++) {
		positions[i] = {
			x: startX + i * (NODE_WIDTH + NODE_SPACING_X),
			y: startY
		};
	}
	return positions;
}

function ConnectionLine({ from, to }: { from: NodePosition; to: NodePosition }) {
	const fromX = from.x + NODE_WIDTH;
	const fromY = from.y + NODE_HEIGHT / 2;
	const toX = to.x;
	const toY = to.y + NODE_HEIGHT / 2;
	const midX = (fromX + toX) / 2;
	const path = `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`;

	return (
		<svg className="pointer-events-none absolute inset-0 overflow-visible" style={{ zIndex: 0 }}>
			<defs>
				<marker
					id="arrowhead-light"
					markerWidth="10"
					markerHeight="7"
					refX="9"
					refY="3.5"
					orient="auto"
				>
					<polygon points="0 0, 10 3.5, 0 7" className="fill-gray-500" />
				</marker>
			</defs>
			<path
				d={path}
				fill="none"
				className="stroke-gray-300"
				strokeWidth="2"
				markerEnd="url(#arrowhead-light)"
			/>
		</svg>
	);
}

function DraggableNode({
	step,
	stepIndex,
	position,
	availableTools,
	onUpdate,
	onRemove,
	onPositionChange,
	isFirst,
	zoom
}: {
	step: InstructionStep;
	stepIndex: number;
	position: NodePosition;
	availableTools: AgentTool[];
	onUpdate: (index: number, step: InstructionStep) => void;
	onRemove: (index: number) => void;
	onPositionChange: (index: number, position: NodePosition) => void;
	isFirst: boolean;
	zoom: number;
}) {
	const [isDragging, setIsDragging] = useState(false);
	const dragStartRef = useRef({ x: 0, y: 0, nodeX: 0, nodeY: 0 });
	const tool = step.type === 'tool' ? availableTools.find((t) => t.id === step.toolId) : null;
	const isInstruction = step.type === 'instruction';

	const handleMouseDown = (e: React.MouseEvent) => {
		if ((e.target as HTMLElement).closest('textarea, button')) return;
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
		dragStartRef.current = {
			x: e.clientX,
			y: e.clientY,
			nodeX: position.x,
			nodeY: position.y
		};
	};

	useEffect(() => {
		if (!isDragging) return;

		const handleMouseMove = (e: MouseEvent) => {
			const dx = (e.clientX - dragStartRef.current.x) / zoom;
			const dy = (e.clientY - dragStartRef.current.y) / zoom;
			onPositionChange(stepIndex, {
				x: dragStartRef.current.nodeX + dx,
				y: dragStartRef.current.nodeY + dy
			});
		};

		const handleMouseUp = () => {
			setIsDragging(false);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isDragging, stepIndex, onPositionChange, zoom]);

	const nodeClasses = `group pointer-events-auto absolute rounded-lg transition-shadow duration-200 select-none ${
		isDragging ? 'z-50 cursor-grabbing shadow-lg' : 'cursor-grab shadow-sm'
	} ${isInstruction ? 'border-2 border-purple-300 bg-white' : 'border-2 border-teal-300 bg-white'}`;

	const headerClasses = `flex items-center gap-2 rounded-t-md px-2.5 py-2 ${
		isInstruction
			? 'bg-purple-50 border-b border-purple-100'
			: 'bg-teal-50 border-b border-teal-100'
	}`;

	const badgeClasses = `flex h-5 w-5 items-center justify-center rounded text-xs font-semibold text-white shrink-0 ${
		isInstruction ? 'bg-purple-500' : 'bg-teal-500'
	}`;

	return (
		<div
			className={nodeClasses}
			style={{
				left: position.x,
				top: position.y,
				width: NODE_WIDTH
			}}
			onMouseDown={handleMouseDown}
		>
			<div className={headerClasses}>
				<div className={badgeClasses}>{stepIndex + 1}</div>
				{isInstruction ? (
					<MessageSquare className="h-4 w-4 text-gray-600" />
				) : (
					<Tool className="h-4 w-4 text-gray-600" />
				)}
				<span className="flex-1 truncate text-sm font-medium text-gray-700">
					{isInstruction ? (isFirst ? 'Trigger' : 'Instruction') : tool?.name || 'Tool'}
				</span>
				{!isInstruction && tool && <TextBadge text={tool.category} type="primary" size="sm" />}
				<button
					onClick={(e) => {
						e.stopPropagation();
						onRemove(stepIndex);
					}}
					className="h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-100 hover:text-red-600 rounded"
				>
					<X className="h-3 w-3" />
				</button>
			</div>
			<div className="rounded-b-md bg-white p-2">
				{isInstruction ? (
					<TextArea
						id={`step-${stepIndex}`}
						name={`step-${stepIndex}`}
						value={step.text || ''}
						onChange={(e) => onUpdate(stepIndex, { ...step, text: e.target.value })}
						placeholder={isFirst ? 'When should this flow trigger?' : 'Instruction...'}
						rows={2}
					/>
				) : (
					<Tooltip content={tool?.description}>
						<p className="line-clamp-2 text-xs text-gray-600 py-0.5">{tool?.description}</p>
					</Tooltip>
				)}
			</div>
			<div
				className={`absolute top-1/2 left-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white ${
					isInstruction ? 'border-purple-300' : 'border-teal-300'
				}`}
			/>
			<div
				className={`absolute top-1/2 right-0 h-3 w-3 translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white ${
					isInstruction ? 'border-purple-300' : 'border-teal-300'
				}`}
			/>
		</div>
	);
}

function SidebarItem({
	icon: Icon,
	title,
	description,
	badge,
	onClick,
	variant = 'tool'
}: {
	icon: React.ElementType;
	title: string;
	description?: string;
	badge?: string;
	onClick?: () => void;
	variant?: 'tool' | 'variable';
}) {
	const baseClasses =
		'flex items-start gap-2.5 rounded-lg border p-2.5 transition-all border-gray-200 bg-white';
	const hoverClasses =
		variant === 'tool'
			? 'hover:border-teal-300 hover:bg-green-50'
			: 'hover:border-yellow-300 hover:bg-yellow-50';
	const cursorClass = onClick ? 'cursor-pointer' : '';

	return (
		<Tooltip content={description ? `${title}: ${description}` : undefined}>
			<div onClick={onClick} className={`${baseClasses} ${hoverClasses} ${cursorClass}`}>
				<Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-600" />
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="truncate text-sm font-medium text-gray-700">{title}</span>
						{badge && <TextBadge text={badge} type="primary" size="sm" />}
					</div>
					{description && (
						<p className="mt-0.5 line-clamp-1 text-xs text-gray-500">{description}</p>
					)}
				</div>
			</div>
		</Tooltip>
	);
}

function CollapsibleSection({
	title,
	icon: Icon,
	children,
	defaultOpen = true,
	count
}: {
	title: string;
	icon: React.ElementType;
	children: React.ReactNode;
	defaultOpen?: boolean;
	count?: number;
}) {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	return (
		<div className="border-b border-gray-100 last:border-b-0">
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="flex w-full items-center gap-2 px-3 py-2.5 transition-colors hover:bg-gray-50"
			>
				{isOpen ? (
					<ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
				) : (
					<ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
				)}
				<Icon className="h-4 w-4 text-gray-500 shrink-0" />
				<span className="flex-1 text-left text-sm font-medium text-gray-700 truncate">{title}</span>
				{count !== undefined && (
					<span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{count}</span>
				)}
			</button>
			{isOpen && <div className="space-y-1.5 px-3 pb-3">{children}</div>}
		</div>
	);
}

export function ToolInstructionBuilder({
	isOpen,
	onClose,
	availableTools,
	contextVariables = [],
	onSave,
	initialData
}: ToolInstructionBuilderProps) {
	const t = useTranslations('ManageAgents');
	const canvasRef = useRef<HTMLDivElement>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [steps, setSteps] = useState<InstructionStep[]>(() => {
		if (initialData?.steps && Array.isArray(initialData.steps) && initialData.steps.length > 0) {
			return [...initialData.steps];
		}
		return [];
	});

	const storageKey = `${STORAGE_KEY_PREFIX}${initialData?.id || 'new'}`;

	const [canvasState, setCanvasState] = useState<CanvasState>(() => {
		if (typeof window !== 'undefined') {
			const saved = localStorage.getItem(storageKey);
			if (saved) {
				try {
					return JSON.parse(saved);
				} catch {
					return {
						positions: getDefaultPositions(steps.length),
						zoom: 1,
						pan: { x: 0, y: 0 }
					};
				}
			}
		}
		return {
			positions: getDefaultPositions(steps.length),
			zoom: 1,
			pan: { x: 0, y: 0 }
		};
	});

	const [isPanning, setIsPanning] = useState(false);
	const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

	useEffect(() => {
		if (typeof window !== 'undefined' && steps.length > 0) {
			localStorage.setItem(storageKey, JSON.stringify(canvasState));
		}
	}, [canvasState, storageKey, steps.length]);

	useEffect(() => {
		const missingPositions = steps.reduce(
			(acc, _, index) => {
				if (!canvasState.positions[index]) {
					const lastPosition = canvasState.positions[index - 1] || {
						x: 100 - NODE_WIDTH - NODE_SPACING_X,
						y: 150
					};
					acc[index] = {
						x: lastPosition.x + NODE_WIDTH + NODE_SPACING_X,
						y: lastPosition.y
					};
				}
				return acc;
			},
			{} as Record<number, NodePosition>
		);

		if (Object.keys(missingPositions).length > 0) {
			setCanvasState((prev) => ({
				...prev,
				positions: { ...prev.positions, ...missingPositions }
			}));
		}
	}, [steps.length, canvasState.positions]);

	const usedToolIds = new Set(
		steps.filter((s) => s.type === 'tool' && s.toolId).map((s) => s.toolId!)
	);
	const availableToolsList = availableTools.filter((t) => !usedToolIds.has(t.id));

	const handlePositionChange = useCallback((index: number, position: NodePosition) => {
		setCanvasState((prev) => ({
			...prev,
			positions: { ...prev.positions, [index]: position }
		}));
	}, []);

	const handleZoom = useCallback((delta: number) => {
		setCanvasState((prev) => ({
			...prev,
			zoom: Math.min(2, Math.max(0.25, prev.zoom + delta))
		}));
	}, []);

	const handleResetView = useCallback(() => {
		setCanvasState((prev) => ({
			...prev,
			zoom: 1,
			pan: { x: 0, y: 0 }
		}));
	}, []);

	const handleFitToScreen = useCallback(() => {
		if (steps.length === 0 || !canvasRef.current) return;

		const positions = Object.values(canvasState.positions);
		if (positions.length === 0) return;

		const minX = Math.min(...positions.map((p) => p.x));
		const maxX = Math.max(...positions.map((p) => p.x + NODE_WIDTH));
		const minY = Math.min(...positions.map((p) => p.y));
		const maxY = Math.max(...positions.map((p) => p.y + NODE_HEIGHT));

		const contentWidth = maxX - minX + 100;
		const contentHeight = maxY - minY + 100;

		const canvas = canvasRef.current;
		const canvasWidth = canvas.clientWidth;
		const canvasHeight = canvas.clientHeight;

		const scaleX = canvasWidth / contentWidth;
		const scaleY = canvasHeight / contentHeight;
		const newZoom = Math.min(scaleX, scaleY, 1.5);

		const centerX = (minX + maxX) / 2;
		const centerY = (minY + maxY) / 2;

		setCanvasState((prev) => ({
			...prev,
			zoom: newZoom,
			pan: {
				x: canvasWidth / 2 / newZoom - centerX,
				y: canvasHeight / 2 / newZoom - centerY
			}
		}));
	}, [steps.length, canvasState.positions]);

	const handleCanvasMouseDown = (e: React.MouseEvent) => {
		setIsPanning(true);
		panStartRef.current = {
			x: e.clientX,
			y: e.clientY,
			panX: canvasState.pan.x,
			panY: canvasState.pan.y
		};
	};

	useEffect(() => {
		if (!isPanning) return;

		const handleMouseMove = (e: MouseEvent) => {
			const dx = (e.clientX - panStartRef.current.x) / canvasState.zoom;
			const dy = (e.clientY - panStartRef.current.y) / canvasState.zoom;
			setCanvasState((prev) => ({
				...prev,
				pan: {
					x: panStartRef.current.panX + dx,
					y: panStartRef.current.panY + dy
				}
			}));
		};

		const handleMouseUp = () => {
			setIsPanning(false);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isPanning, canvasState.zoom]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const handleWheel = (e: WheelEvent) => {
			if (e.ctrlKey || e.metaKey) {
				e.preventDefault();
				const delta = e.deltaY > 0 ? -0.03 : 0.03;
				setCanvasState((prev) => ({
					...prev,
					zoom: Math.min(2, Math.max(0.25, prev.zoom + delta))
				}));
			}
		};

		canvas.addEventListener('wheel', handleWheel, { passive: false });
		return () => canvas.removeEventListener('wheel', handleWheel);
	}, []);

	const handleAddTool = (toolId: string) => {
		const newIndex = steps.length;
		const lastPosition = canvasState.positions[newIndex - 1] || {
			x: 100 - NODE_WIDTH - NODE_SPACING_X,
			y: 150
		};

		setCanvasState((prev) => ({
			...prev,
			positions: {
				...prev.positions,
				[newIndex]: {
					x: lastPosition.x + NODE_WIDTH + NODE_SPACING_X,
					y: lastPosition.y
				}
			}
		}));

		setSteps([...steps, { type: 'tool', toolId }]);
	};

	const handleAddInstruction = () => {
		const newIndex = steps.length;
		const lastPosition = canvasState.positions[newIndex - 1] || {
			x: 100 - NODE_WIDTH - NODE_SPACING_X,
			y: 150
		};

		setCanvasState((prev) => ({
			...prev,
			positions: {
				...prev.positions,
				[newIndex]: {
					x: lastPosition.x + NODE_WIDTH + NODE_SPACING_X,
					y: lastPosition.y
				}
			}
		}));

		setSteps([...steps, { type: 'instruction', text: '' }]);
	};

	const handleUpdateStep = (index: number, updatedStep: InstructionStep) => {
		setSteps((items) => items.map((item, i) => (i === index ? updatedStep : item)));
	};

	const handleRemoveStep = (index: number) => {
		setSteps((items) => items.filter((_, i) => i !== index));
		setCanvasState((prev) => {
			const newPositions: Record<number, NodePosition> = {};
			Object.entries(prev.positions).forEach(([key, value]) => {
				const keyNum = parseInt(key);
				if (keyNum < index) {
					newPositions[keyNum] = value;
				} else if (keyNum > index) {
					newPositions[keyNum - 1] = value;
				}
			});
			return { ...prev, positions: newPositions };
		});
	};

	const handleSave = async () => {
		if (steps.length === 0) return;
		if (steps.some((s) => s.type === 'instruction' && !s.text?.trim())) return;
		if (steps[0]?.type !== 'instruction' || !steps[0]?.text?.trim()) return;

		setIsSaving(true);
		try {
			await onSave({ steps });
			onClose();
		} finally {
			setIsSaving(false);
		}
	};

	const handleClose = () => {
		if (!isSaving) {
			onClose();
		}
	};

	const hasValidSteps =
		steps.length > 0 &&
		steps[0]?.type === 'instruction' &&
		!!steps[0]?.text?.trim() &&
		steps.every((s) => {
			if (s.type === 'tool') return !!s.toolId;
			return !!s.text?.trim();
		});

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
			<div className="relative h-[95vh] w-[95vw] overflow-hidden rounded-lg border border-gray-200 bg-gray-50 flex flex-col">
				<div className="border-b border-gray-200 bg-white px-5 py-4 flex items-center justify-between">
					<div className="flex items-center gap-2">
						<GitBranch className="h-5 w-5 text-gray-700" />
						<Typography type="text" weight="semibold">
							{t('ConfigureToolInstruction')}
						</Typography>
					</div>
					<button
						onClick={handleClose}
						disabled={isSaving}
						className="h-8 w-8 flex items-center justify-center rounded hover:bg-gray-100"
					>
						<X className="h-5 w-5 text-gray-600" />
					</button>
				</div>

				<div className="flex flex-1 overflow-hidden">
					<div className="flex flex-1 flex-col overflow-hidden">
						<div className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3">
							<div className="flex items-center gap-3">
								<div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
									<Button
										color="gray"
										size="auto"
										textOnly
										onClick={() => handleZoom(-0.05)}
										disabled={canvasState.zoom <= 0.25}
										startIcon={<ZoomOut className="h-4 w-4" />}
										label=""
									/>
									<span className="px-2 text-xs text-gray-600">
										{Math.round(canvasState.zoom * 100)}%
									</span>
									<Button
										color="gray"
										size="auto"
										textOnly
										onClick={() => handleZoom(0.05)}
										disabled={canvasState.zoom >= 2}
										startIcon={<ZoomIn className="h-4 w-4" />}
										label=""
									/>
								</div>
								<Button
									color="gray"
									size="auto"
									textOnly
									onClick={handleResetView}
									startIcon={<RotateCcw className="h-3 w-3" />}
									label="Reset"
								/>
								<Button
									color="gray"
									size="auto"
									textOnly
									onClick={handleFitToScreen}
									startIcon={<Maximize className="h-3 w-3" />}
									label="Fit"
								/>
							</div>

							<Button
								color="primary"
								size="auto"
								onClick={handleAddInstruction}
								startIcon={<Plus className="h-3.5 w-3.5" />}
								label="Add Instruction"
							/>
						</div>

						<div
							ref={canvasRef}
							className={`relative flex-1 overflow-hidden bg-gray-50 ${
								isPanning ? 'cursor-grabbing' : 'cursor-grab'
							}`}
							style={{
								backgroundImage:
									'radial-gradient(circle, rgb(232 230 227 / 1) 1px, transparent 1px)',
								backgroundSize: `${20 * canvasState.zoom}px ${20 * canvasState.zoom}px`,
								backgroundPosition: `${canvasState.pan.x * canvasState.zoom}px ${canvasState.pan.y * canvasState.zoom}px`
							}}
							onMouseDown={handleCanvasMouseDown}
						>
							{steps.length === 0 ? (
								<div className="pointer-events-none flex h-full items-center justify-center">
									<div className="pointer-events-auto text-center">
										<div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white">
											<GitBranch className="h-8 w-8 text-gray-400" />
										</div>
										<p className="mb-4 text-sm text-gray-500">No steps in this flow yet</p>
										<Button
											onClick={handleAddInstruction}
											color="primary"
											size="auto"
											startIcon={<Plus className="h-4 w-4" />}
											label="Add Trigger Instruction"
										/>
									</div>
								</div>
							) : (
								<div
									className="pointer-events-none absolute inset-0"
									style={{
										transform: `scale(${canvasState.zoom})`,
										transformOrigin: '0 0'
									}}
								>
									<div
										className="pointer-events-none"
										style={{
											transform: `translate(${canvasState.pan.x}px, ${canvasState.pan.y}px)`
										}}
									>
										{steps.map((_, index) => {
											if (index === 0) return null;
											const from = canvasState.positions[index - 1];
											const to = canvasState.positions[index];
											if (!from || !to) return null;
											return <ConnectionLine key={`conn-${index}`} from={from} to={to} />;
										})}
										{steps.map((step, stepIndex) => (
											<DraggableNode
												key={stepIndex}
												step={step}
												stepIndex={stepIndex}
												position={canvasState.positions[stepIndex] || { x: 0, y: 0 }}
												availableTools={availableTools}
												onUpdate={handleUpdateStep}
												onRemove={handleRemoveStep}
												onPositionChange={handlePositionChange}
												isFirst={stepIndex === 0}
												zoom={canvasState.zoom}
											/>
										))}
									</div>
								</div>
							)}
						</div>
					</div>

					<div className="flex w-72 flex-col overflow-hidden border-l border-gray-200 bg-white">
						<div className="flex-1 overflow-y-auto py-2">
							{contextVariables.length > 0 && (
								<CollapsibleSection
									title="Context Variables"
									icon={Code}
									count={contextVariables.length}
									defaultOpen={false}
								>
									{contextVariables.map((variable) => (
										<SidebarItem
											key={variable.id}
											icon={Code}
											title={variable.name}
											description={variable.description}
											badge={variable.data_type}
											variant="variable"
										/>
									))}
								</CollapsibleSection>
							)}
							<CollapsibleSection
								title="Available Tools"
								icon={Tool}
								count={availableToolsList.length}
								defaultOpen={true}
							>
								{availableToolsList.length === 0 ? (
									<p className="py-2 text-center text-xs text-gray-500">
										{steps.some((s) => s.type === 'tool')
											? 'All tools added'
											: 'No tools available'}
									</p>
								) : (
									availableToolsList.map((tool) => (
										<SidebarItem
											key={tool.id}
											icon={Tool}
											title={tool.name}
											description={tool.description}
											badge={tool.category}
											onClick={() => handleAddTool(tool.id)}
											variant="tool"
										/>
									))
								)}
							</CollapsibleSection>
						</div>
					</div>
				</div>

				<div className="border-t border-gray-200 bg-white px-5 py-4">
					<div className="flex w-full items-center justify-between">
						<p className="text-sm text-gray-500">
							{steps.length} step{steps.length !== 1 ? 's' : ''} • Drag canvas to pan • Ctrl+scroll
							to zoom
						</p>
						<div className="flex gap-2">
							<Button
								color="gray"
								size="auto"
								onClick={handleClose}
								disabled={isSaving}
								label={t('Cancel')}
							/>
							<Button
								color="primary"
								size="auto"
								onClick={handleSave}
								disabled={isSaving || !hasValidSteps}
								loading={isSaving}
								label={isSaving ? t('SavingInstruction') : t('Save')}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
